import React, { FunctionComponent, useEffect, useMemo } from 'react';
import '../productPage/producthome.css';
import { Box, SxProps } from '@mui/material';
import * as styles from '../productPage/tableComponents/ProductPage_style';
import { useLocation, useNavigate } from 'react-router-dom';
import type { AboutInvestorRelationsContent } from '../../graphql/pimcore/modules/siteContent/types/AboutInvestorRelationsContent';
import BreadCrumbs from '../common/BreadCrumbs';
import OverviewPage from './OverviewPage';
import { useResponsive } from '../../contexts/ResponsiveContext';
import TechnicalResources from '../automotivePage/TechnicalResources';
import { PrefilteredResourceLibraryItemsProvider } from '../../graphql/pimcore/modules/resourceLibrary/contexts/PrefilteredResourceLibraryItemsProvider';
import TabsComponent, { GetResponsiveWidth } from '../common/TabsComponent';
import OutterWrapper from '@/components/common/OutterWrapper';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`product-tabpanel-${index}`}
            aria-labelledby={`product-tab-${index}`}
            {...other}
        >
            {value === index && <Box>{children}</Box>}
        </div>
    );
}

export const getTabTypography = (isScreenMd: boolean, isScreenSm: boolean): SxProps => ({
    fontSize: isScreenMd || isScreenSm ? 14 : 18,
    fontFamily: 'Poppins',
});

interface InvestorRelationHomeProps {
    cmsContent: AboutInvestorRelationsContent;
}

const InvestorRelationHome: FunctionComponent<InvestorRelationHomeProps> = ({ cmsContent }) => {
    const [url, setUrl] = React.useState(0);
    const [openMenu, setOpenMenu] = React.useState(false); // 用於控制小螢幕時的展開/收合

    const { isScreenMd, isScreenSm, isScreenXs } = useResponsive();

    const tabTypography = getTabTypography(isScreenMd, isScreenSm);

    const location = useLocation();
    const navigate = useNavigate();

    const pathMap = useMemo(
        () => [
            '/About/InvestorRelations/Overview',
            '/About/InvestorRelations/FinancialReports',
            '/About/InvestorRelations/InvestorConference',
            '/About/InvestorRelations/ShareholderMeetings',
            '/About/InvestorRelations/Newsroom',
        ],
        []
    );

    useEffect(() => {
        const newUrl = pathMap.indexOf(location.pathname);
        setUrl(newUrl >= 0 ? newUrl : 0);
    }, [location.pathname, pathMap]);

    const handleChange = (event: React.SyntheticEvent | null, newUrl: number) => {
        setUrl(newUrl);
        navigate(pathMap[newUrl] || pathMap[0]!);
    };

    const menuItems = [
        { label: cmsContent.Overview.title.content, value: 0 },
        { label: cmsContent.FinancialReports.title.content, value: 1 },
        { label: cmsContent.InvestorConference.title.content, value: 2 },
        { label: cmsContent.ShareholderMeetings.title.content, value: 3 },
        { label: cmsContent.Resources.title.content, value: 4 },
    ];

    return (
        <OutterWrapper
            componentName={'overview'}
            pathname={location.pathname}
            pageTitle={cmsContent.pageTitle.content}
            bannerImageUrl={cmsContent.pageTitleBackgroundImage.imageUrl}
            breadcrumbsComponent={<BreadCrumbs navigationPath="/Home/About/InvestorRelations" />}
            specialPageSubTitle={cmsContent.pageSubtitle.content
                .split('\n')
                .map((line, index, arr) => (
                    <React.Fragment key={index}>
                        {line}
                        {index < arr.length - 1 && <br />}
                    </React.Fragment>
                ))}
        >
            {
                <Box
                    sx={{
                        zIndex: 1,
                        width: '100%',
                        backgroundColor: isScreenSm || isScreenXs ? 'white' : '#7A7C81',
                    }}
                    data-testid={'outter_box'}
                >
                    <TabsComponent
                        isMenuExpandMoreIcon
                        url={url}
                        setOpenMenu={setOpenMenu}
                        openMenu={openMenu}
                        handleChange={handleChange}
                        menuItems={menuItems}
                        tabTypography={tabTypography}
                    />

                    <CustomTabPanel value={url} index={0}>
                        <OverviewPage cmsContent={cmsContent.Overview} />
                    </CustomTabPanel>
                    <CustomTabPanel value={url} index={1}>
                        {location.pathname.includes('FinancialReports') && (
                            <PrefilteredResourceLibraryItemsProvider
                                initialFilters={{
                                    // On change of resourceTypeKeys, please also update the related Pimcore role
                                    // - Full_About-InvestorRelations role
                                    resourceTypeKeys: ['Financial/Financial Report (Quarterly)'],
                                }}
                            >
                                <TechnicalResources backgroundColor={'white'} />
                            </PrefilteredResourceLibraryItemsProvider>
                        )}
                    </CustomTabPanel>
                    <CustomTabPanel value={url} index={2}>
                        {location.pathname.includes('InvestorConference') && (
                            <PrefilteredResourceLibraryItemsProvider
                                initialFilters={{
                                    // On change of resourceTypeKeys, please also update the related Pimcore role
                                    // - Full_About-InvestorRelations role
                                    resourceTypeKeys: [
                                        // 'Financial/Investor Conference (Presentation)',
                                        'Financial/Investor Conference',
                                        'Financial/Earnings Conference',
                                        // 'Financial/Investor Conference (Audio)',
                                    ],
                                }}
                            >
                                <TechnicalResources backgroundColor={'white'} />
                            </PrefilteredResourceLibraryItemsProvider>
                        )}
                    </CustomTabPanel>

                    {location.pathname.includes('ShareholderMeetings') && (
                        <PrefilteredResourceLibraryItemsProvider
                            initialFilters={{
                                // On change of resourceTypeKeys, please also update the related Pimcore role
                                // - Full_About-InvestorRelations role
                                resourceTypeKeys: [
                                    // 'Financial/Shareholder Information (will be retired)',
                                    'Financial/Shareholder Meeting',
                                    // 'Financial/Shareholder Meeting Minutes',
                                    // 'Financial/Shareholder Meeting Notice',
                                    // 'Financial/Investor Conference (Audio)',
                                    // 'Financial/Investor Conference (Presentation)',
                                ],
                            }}
                        >
                            <TechnicalResources backgroundColor={'white'} />
                        </PrefilteredResourceLibraryItemsProvider>
                    )}
                    <CustomTabPanel value={url} index={3}></CustomTabPanel>
                    {location.pathname.includes('Newsroom') && (
                        <PrefilteredResourceLibraryItemsProvider
                            // On change of resourceTypeKeys, please also update the related Pimcore role
                            // - Full_About-InvestorRelations role
                            initialFilters={{
                                resourceTypeKeys: [
                                    'News',
                                    'News/Corporate',
                                    'News/Financial',
                                    'News/Product',
                                ],
                            }}
                        >
                            <TechnicalResources backgroundColor={'white'} />
                        </PrefilteredResourceLibraryItemsProvider>
                    )}
                </Box>
            }
        </OutterWrapper>
    );
};

export default InvestorRelationHome;
